import React, { useState } from 'react';
import { string, object, func } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { createSavedSearch, addSavedSearch } from './savedSearchUtils';
import css from './SaveSearchButton.module.css';

/**
 * SaveSearchButton — saves the current search filters for quick re-use.
 *
 * Usage (in SearchPage or SearchFiltersPanel):
 *   <SaveSearchButton
 *     currentUser={currentUser}
 *     searchQuery={{ keywords: 'bike', pub_category: 'city' }}
 *     onSave={async (updatedProtectedData) => {
 *       await dispatch(updateProfile({ protectedData: updatedProtectedData }));
 *     }}
 *   />
 */
const SaveSearchButton = ({ currentUser, searchQuery, onSave, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [notify, setNotify] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!currentUser) return null;

  const handleSave = async () => {
    if (!name.trim() || isLoading) return;
    setIsLoading(true);
    try {
      const saved = createSavedSearch(name, searchQuery, notify);
      const updated = addSavedSearch(currentUser, saved);
      if (onSave) await onSave(updated);
      setIsOpen(false);
      setName('');
      setNotify(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classNames(css.root, className)}>
      {!isOpen ? (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className={css.trigger}
        >
          ⭐ <FormattedMessage id="SaveSearchButton.save" />
        </button>
      ) : (
        <div className={css.form}>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Search name..."
            className={css.input}
            autoFocus
          />
          <label className={css.notifyLabel}>
            <input
              type="checkbox"
              checked={notify}
              onChange={e => setNotify(e.target.checked)}
            />
            {' '}
            <FormattedMessage id="SaveSearchButton.notifyByEmail" />
          </label>
          <div className={css.formActions}>
            <button
              type="button"
              onClick={handleSave}
              disabled={!name.trim() || isLoading}
              className={css.saveBtn}
            >
              <FormattedMessage id="SaveSearchButton.confirm" />
            </button>
            <button type="button" onClick={() => setIsOpen(false)} className={css.cancelBtn}>
              <FormattedMessage id="SaveSearchButton.cancel" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

SaveSearchButton.defaultProps = { className: null, onSave: null };
SaveSearchButton.propTypes = { currentUser: object, searchQuery: object, onSave: func, className: string };

export default SaveSearchButton;
